import React, { useState, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { UsersAppointmentsGet } from 'apiServices/reducer/apiService';
import { FaTrash } from 'react-icons/fa';
import '../../../styles/user.css';
import { deleteUserAppointmentDetails } from 'apiServices/reducer/dialogApiService';
import CustomSuccessModal from '../../../components/CustomSuccessModal';
import { showPmrDeleteConfirm } from '../../../utils/swalUtils';

const ManageAppointmentsModal = ({ show, onHide, userId, refreshAppointments }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.apiService?.token);
    const [appointments, setAppointments] = useState([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const appointmentCountBeforeDelete = useRef(0);

    // Flags to prevent double execution (like userLandingPage.jsx)
    const [isDeleteProcessing, setIsDeleteProcessing] = useState(false);

    // Refs to track modal state (like userLandingPage.jsx)
    const deleteModalShownRef = useRef(false);

    const fetchAppointments = async () => {
        try {
            const response = await dispatch(UsersAppointmentsGet({ UserID: userId, token }));
            if (response?.payload && Array.isArray(response.payload)) {
                setAppointments(response.payload);
            } else {
                setAppointments([]);
            }
        } catch (err) {
            console.error("Failed to fetch appointments:", err);
        }
    };

    React.useEffect(() => {
        if (show) {
            fetchAppointments();
            // Reset modal states when modal opens
            setShowSuccessModal(false);
            setIsDeleteProcessing(false);
            deleteModalShownRef.current = false;
        }
    }, [show]);

    // Group appointments by location
    const groupAppointmentsByLocation = (appointments) => {
        const grouped = {};
        appointments.forEach(appt => {
            const locationKey = appt.location_name || 'Unknown Location';
            if (!grouped[locationKey]) {
                grouped[locationKey] = {
                    locationName: locationKey,
                    locationDetails: appt.location_details,
                    appointments: []
                };
            }
            grouped[locationKey].appointments.push(appt);
        });
        return Object.values(grouped);
    };

    const handleDelete = async (appt) => {
        // Prevent double execution (like userLandingPage.jsx)
        if (isDeleteProcessing || deleteModalShownRef.current) {
            console.log("Delete already in progress or modal already shown, skipping...");
            return;
        }

        appointmentCountBeforeDelete.current = appointments.length; // Track before delete
        showPmrDeleteConfirm({
            message: 'You want to delete this appointment? This action cannot be undone.',
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsDeleteProcessing(true);
                deleteModalShownRef.current = true;

                // Reset modal state first to prevent double showing (like userLandingPage.jsx)
                setShowSuccessModal(false);

                try {
                    const formBody = {
                        ApptID: appt.appt_id,
                        UserID: userId,
                        ApptLocID: appt.appt_loc_id,
                        token: token,
                    };
                    const response = await dispatch(deleteUserAppointmentDetails(formBody));
                    if (response?.payload) {
                        // Use setTimeout to ensure state is properly reset before showing modal (like userLandingPage.jsx)
                        setTimeout(() => {
                            setSuccessMessage(response?.payload?.OutputMessage || 'Appointment deleted successfully!');
                            setShowSuccessModal(true);
                            setIsDeleteProcessing(false);
                        }, 100);
                        setAppointments(prev => prev.filter(a => a.appt_id !== appt.appt_id));
                        refreshAppointments();
                        try {
                            window.dispatchEvent(new CustomEvent('pmr:appointments-changed', {
                                detail: { action: 'delete', apptId: appt.appt_id },
                            }));
                        } catch (e) { /* ignore */ }
                    } else {
                        setIsDeleteProcessing(false);
                        deleteModalShownRef.current = false;
                    }
                } catch (error) {
                    setIsDeleteProcessing(false);
                    deleteModalShownRef.current = false;
                }
            }
        });
    };

    const handleSuccessModalClose = () => {
        setShowSuccessModal(false);
        setSuccessMessage('');
        deleteModalShownRef.current = false;
        // If there was only one appointment before deletion, close the Manage Appointments modal
        if (appointmentCountBeforeDelete.current === 1) {
            onHide();
        }
    };

    const groupedAppointments = groupAppointmentsByLocation(appointments);

    return (
        <Modal show={show} onHide={onHide} centered size="lg" backdrop="static" dialogClassName="wider-modal" keyboard={false} enforceFocus={false}>
            <Modal.Header closeButton className='modal-header-bg'>
                <Modal.Title className='text-capitalize'>Manage Upcoming Appointments</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {appointments.length === 0 ? (
                    <p className="text-muted">No appointments found.</p>
                ) : (
                    <div className="appointment-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {groupedAppointments.map((locationGroup, locationIndex) => (
                            <div key={locationIndex} className="location-group mb-4 p-3 border rounded">
                                <h6 className="fw-bold text-primary mb-3">{locationGroup.locationName}</h6>
                                {/* {locationGroup.locationDetails && (
                                    // <div className="mb-3 text-muted">
                                    //     <small>
                                    //         {locationGroup.locationDetails
                                    //             .replace(/\r\n/g, ', ')
                                    //             .split(',')
                                    //             .map(part => part.trim())
                                    //             .filter(part => part && part.toLowerCase() !== 'string')
                                    //             .join(', ')}
                                    //     </small>
                                    // </div>
                                )} */}
                                {locationGroup.appointments.map((appt) => (
                                    <div key={appt.appt_id} className="appointment-item mb-2 p-2 border-start border-primary" style={{ borderLeftWidth: '3px' }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <div className="d-flex align-items-center mb-1">
                                                    <span className="me-2 fw-bold">Date:</span>
                                                    <span>{appt.appt_date || "N/A"}</span>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 fw-bold">Time:</span>
                                                    <span>{appt.appt_start || "N/A"} to {appt.appt_end || "N/A"}</span>
                                                </div>
                                            </div>
                                            <Button
                                                variant="link"
                                                className="p-0"
                                                onClick={() => handleDelete(appt)}
                                                title="Delete Appointment"
                                                style={{ boxShadow: "none" }}
                                            >
                                                <FaTrash className="text-danger" style={{ fontSize: 20 }} />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className='modal-header-bg'>
                <Button variant="danger" className='text-capitalize ' onClick={onHide}>Close</Button>
            </Modal.Footer>
            <CustomSuccessModal
                show={showSuccessModal}
                onHide={handleSuccessModalClose}
                message={successMessage}
            />
        </Modal>
    );
};

export default ManageAppointmentsModal;